import {TileSelectionComponent} from "./tile-selection-component";

export class TileSelectionManager {
  static MAX_TILE_SELECTIONS = 3;

  /** @type {TilesData} */ data;
  /** @type {HTMLElement} */ tileSelectionsRootElement;
  /** @type {HTMLElement} */ addTileSelectionElement;

  /** @type {NodeListOf<Element>} */ titleElements;
  /** @type {NodeListOf<Element>} */ articleNumberElements;
  /** @type {NodeListOf<Element>} */ companyElements;
  /** @type {NodeListOf<Element>} */ factoryElements;

  /** @type {TileSelectionComponent[]} */ tileSelectionComponents = [];

  /**
   * Initialisation of the tile selection process for the sample tile order.
   *
   * @param {TilesData} data The data containing all tile and surface information.
   * @param {HTMLElement} tileSelectionsRootElement The root element, where all TileSelectionComponents should be rendered.
   * @param {HTMLElement} addTileSelectionElement The box which is responsible for adding more TileSelectionComponents on click.
   * @param {string} querySelectorForAllTitleInputs Selector for all input fields in the outer form containing the title.
   * @param {string} querySelectorForAllArticleNumberInputs Selector for all input fields in the outer form containing the article-number.
   * @param {string} querySelectorForAllCompanyInputs Selector for all input fields in the outer form containing the company.
   * @param {string} querySelectorForAllFactoryInputs Selector for all input fields in the outer form containing the factory.
   */
  constructor(
    data,
    tileSelectionsRootElement,
    addTileSelectionElement,
    querySelectorForAllTitleInputs,
    querySelectorForAllArticleNumberInputs,
    querySelectorForAllCompanyInputs,
    querySelectorForAllFactoryInputs,
  ) {
    this.data = data;
    this.tileSelectionsRootElement = tileSelectionsRootElement;
    this.addTileSelectionElement = addTileSelectionElement;
    this.titleElements = document.querySelectorAll(querySelectorForAllTitleInputs);
    this.articleNumberElements = document.querySelectorAll(querySelectorForAllArticleNumberInputs);
    this.companyElements = document.querySelectorAll(querySelectorForAllCompanyInputs);
    this.factoryElements = document.querySelectorAll(querySelectorForAllFactoryInputs);

    this._addTileSelection();

    this.addTileSelectionElement.addEventListener('click', () => {
      if (this.tileSelectionComponents.length < TileSelectionManager.MAX_TILE_SELECTIONS) {
        this._addTileSelection();
      }
      if (this.tileSelectionComponents.length === TileSelectionManager.MAX_TILE_SELECTIONS) {
        this.addTileSelectionElement.style.display = 'none';
      }
    });
  }

  /**
   * Creates and adds another TileSelectionComponent.
   */
  _addTileSelection() {
    this.tileSelectionComponents.push(
      new TileSelectionComponent(
        this.data,
        this.tileSelectionsRootElement,
        ((self, removeSelection) => this._onSelectCallback(self, removeSelection)),
      )
    );
  }

  /**
   * Called by TileSelectionComponent when a new tile surface is selected or the selection component got removed.
   *
   * @param {TileSelectionComponent} self A reference to the TileSelectionComponent executing the callback.
   * @param {boolean} removeSelection True if the selection component got removed.
   */
  _onSelectCallback(self, removeSelection) {
    if (removeSelection) {
      this.tileSelectionComponents.splice(this.tileSelectionComponents.indexOf(self), 1);
      this.addTileSelectionElement.style.display = 'block';
    }
    this._emptyForm();
    this._writeSelectionsToForm();
  }

  /**
   * Write the values of all existing selection components to outer form fields.
   */
  _writeSelectionsToForm() {
    let index = 0;
    this.tileSelectionComponents.forEach((selection) => {
      const surface = selection.getSelection();

      if (!surface || this.titleElements.length <= index || this.articleNumberElements.length <= index || this.companyElements <= index || this.factoryElements <= index) {
        return;
      }

      this.titleElements[index].setAttribute('value', surface.parent_title + ' - ' + surface.title);
      this.articleNumberElements[index].setAttribute('value', surface.artikelnummer);
      this.companyElements[index].setAttribute('value', surface.firma);
      this.factoryElements[index].setAttribute('value', surface.werk);

      index++;
    });
  }

  /**
   * Remove all values copied into form fields.
   */
  _emptyForm() {
    [this.titleElements, this.articleNumberElements, this.companyElements, this.factoryElements].forEach((elements) => {
      elements.forEach((element) => element.removeAttribute('value'));
    });
  }
}
